import { useEffect, useState } from 'react';
import { Card, Button, Form, Input, Table, Typography, Tag, Space, message, Alert } from 'antd';
import { useNavigate } from 'react-router-dom';
import { apiGet, apiPost, ApiError, type FeedSummary } from '../api/client';

interface CreateFeedValues {
  label: string;
  clientId: string;
  clientSecret: string;
}

export function ConnectFeed() {
  const navigate = useNavigate();
  const [feeds, setFeeds] = useState<FeedSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [paymentRequired, setPaymentRequired] = useState(false);
  const [form] = Form.useForm<CreateFeedValues>();

  async function load() {
    setLoading(true);
    try {
      const res = await apiGet<{ feeds: FeedSummary[] }>('/portal/feeds');
      setFeeds(res.feeds);
    } catch (err) {
      message.error(`Failed to load feeds: ${(err as Error).message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function onSubmit(values: CreateFeedValues) {
    setSubmitting(true);
    setPaymentRequired(false);
    try {
      await apiPost('/portal/feeds', values);
      message.success('Feed connected — first sync queued');
      form.resetFields();
      await load();
    } catch (err) {
      // 402 Payment Required: the backend won't accept feed connections without
      // a card on file (the 7-day trial clock starts at first feed connect, so
      // we need a payment method to charge on day 8). Show inline alert + CTA
      // to /billing instead of a generic toast.
      if (err instanceof ApiError && err.status === 402) {
        setPaymentRequired(true);
      } else {
        message.error(`Failed to create feed: ${(err as Error).message}`);
      }
    } finally {
      setSubmitting(false);
    }
  }

  const statusTag = (status: string) => {
    const color =
      status === 'Active' ? 'green' : status === 'Failing' ? 'red' : status === 'Disabled' ? 'default' : 'blue';
    return <Tag color={color}>{status}</Tag>;
  };

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Typography.Title level={3}>CREA DDF feeds</Typography.Title>
      <Card title="Connect a new feed">
        {paymentRequired && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message="Add a payment method before connecting a feed"
            description="Connecting a feed starts your 7-day free trial — you won't be charged today, but we need a card on file so the subscription can activate when the trial ends. Set this up once and you're done."
            action={
              <Button type="primary" onClick={() => navigate('/billing')}>
                Go to Billing
              </Button>
            }
          />
        )}
        <Alert
          type="info"
          style={{ marginBottom: 16 }}
          message="Where to find your credentials"
          description={
            <div>
              <ol style={{ paddingLeft: 20, margin: 0 }}>
                <li>
                  Sign in to{' '}
                  <a href="https://member.crea.ca" target="_blank" rel="noreferrer">
                    member.crea.ca
                  </a>{' '}
                  with your CREA member credentials.
                </li>
                <li>
                  Open <em>Data Feeds</em>. Select your feed (or create one — type{' '}
                  <em>National Shared Pool</em>, designate a 3rd party with our portal URL).
                </li>
                <li>
                  Under <em>Destination Credentials</em>, CREA shows a <strong>Username</strong> and{' '}
                  <strong>Password</strong>. Paste them below — the Username is your{' '}
                  <code>client_id</code>, the Password is your <code>client_secret</code>.
                </li>
                <li>
                  We exchange them with CREA's identity server for a short-lived access token on
                  every sync. The credentials themselves are stored encrypted in AWS Secrets Manager
                  and never displayed back to you.
                </li>
              </ol>
            </div>
          }
        />
        <Form form={form} layout="vertical" onFinish={onSubmit}>
          <Form.Item label="Label" name="label" rules={[{ required: true }]}>
            <Input placeholder="My main feed" />
          </Form.Item>
          <Form.Item
            label="Username (CREA-issued)"
            name="clientId"
            rules={[{ required: true, min: 10 }]}
          >
            <Input placeholder="e.g. mKtayWuV7JvJHKdF2A6RD1Or" autoComplete="off" />
          </Form.Item>
          <Form.Item
            label="Password (CREA-issued)"
            name="clientSecret"
            rules={[{ required: true, min: 10 }]}
          >
            <Input.Password placeholder="paste your CREA-issued Password — never shown again" autoComplete="off" />
          </Form.Item>
          <Button type="primary" htmlType="submit" loading={submitting}>
            Connect feed
          </Button>
        </Form>
      </Card>

      <Card title="Your feeds">
        <Table
          rowKey="feedId"
          loading={loading}
          dataSource={feeds}
          pagination={false}
          columns={[
            { title: 'Label', dataIndex: 'label' },
            { title: 'Status', dataIndex: 'status', render: statusTag },
            {
              title: 'Last synced',
              dataIndex: 'lastSyncedAt',
              render: (v: string | null) => (v ? new Date(v).toLocaleString() : '—'),
            },
            { title: 'Created', dataIndex: 'createdAt', render: (v: string) => new Date(v).toLocaleString() },
          ]}
        />
      </Card>
    </Space>
  );
}
